import React, { useState } from 'react';
import { LuArrowRightLeft, LuCircleDollarSign, LuExternalLink, LuLink, LuTrash2, LuUserCog, LuX } from 'react-icons/lu';
import { Link, useParams } from 'react-router-dom';

import { Account, LifecycleStatus } from '@ndvr/portfolio-api';

import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { useHouseholdRoutePaths } from 'utils';
import { ChangeAccountOwnerDrawer } from 'views/household/AccountGroupTab/ChangeAccountOwnerDrawer';
import { ChangeLinkedCustodyAccountDrawer } from 'views/household/AccountGroupTab/ChangeLinkedCustodyAccountDrawer';
import { CloseAccountValidationModal } from 'views/household/AccountGroupTab/CloseAccountValidationModal';
import { LinkAccountDrawer } from 'views/household/AccountGroupTab/LinkAccountDrawer';
import { AccountWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { AccountTradeStatusModal } from 'views/portfolio/PortfolioDetails/AccountTradeStatusModal';

import { canLinkAccount } from './LinkAccount';
import { canMoveAccount } from './MoveAccountModal';

interface AccountRowMenuProps {
    account: AccountWithAumIncluded;
    householdGUID?: string;
    onDelete?: (account: Account) => void;
    onMove?: (account: Account) => void;
    showGoToPortfolioAction?: boolean;
    viewAccountUrl?: string;
    showLinkAccountAction?: boolean;
    canChangeTradeStatus?: boolean;
    tradingSuspended?: boolean;
    onChangeTradeStatus?: () => void;
}

export const AccountRowMenu = ({
    account,
    householdGUID,
    onDelete,
    onMove,
    showGoToPortfolioAction = false,
    viewAccountUrl,
    showLinkAccountAction = false,
    canChangeTradeStatus = true,
    tradingSuspended = !!account.tradingSuspendedAt,
    onChangeTradeStatus,
}: AccountRowMenuProps) => {
    const { globalAccountGUID: routeHouseholdGUID } = useParams();
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const globalAccountGUID = householdGUID ?? routeHouseholdGUID;
    if (!globalAccountGUID) {
        throw new Error('AccountRowMenu requires a household GUID');
    }

    const showLiveAccountActions = account.lifecycleStatus === LifecycleStatus.REAL;
    const showDeleteAction = account.lifecycleStatus === LifecycleStatus.DRAFT;

    const [showTradeStatusModal, setShowTradeStatusModal] = useState(false);
    const [showLinkAccountDrawer, setShowLinkAccountDrawer] = useState(false);
    const [showChangeCustodyAccountDrawer, setShowChangeCustodyAccountDrawer] = useState(false);
    const [showChangeAccountOwnerDrawer, setShowChangeAccountOwnerDrawer] = useState(false);
    const [showCloseAccountModal, setShowCloseAccountModal] = useState(false);

    return (
        <>
            <DropdownMenu>
                <EllipsisDropdownMenuTrigger
                    ariaLabel={`Actions for ${account.accountNamePlain ?? account.accountName.plainText}`}
                />
                <DropdownMenuContent align="end">
                    {showGoToPortfolioAction && (
                        <DropdownMenuItem asChild>
                            <Link to={PortfolioPageRoutePath(globalAccountGUID, account.accountGroupGUID)}>
                                <LuExternalLink className="mr-2 h-4 w-4" />
                                Go to portfolio
                            </Link>
                        </DropdownMenuItem>
                    )}
                    {onMove && (
                        <DropdownMenuItem onClick={() => onMove(account)} disabled={!canMoveAccount(account)}>
                            <LuArrowRightLeft className="mr-2 h-4 w-4" />
                            Move to portfolio…
                        </DropdownMenuItem>
                    )}
                    {viewAccountUrl && (
                        <DropdownMenuItem asChild>
                            <Link to={viewAccountUrl}>
                                <LuExternalLink className="mr-2 h-4 w-4" />
                                View account
                            </Link>
                        </DropdownMenuItem>
                    )}
                    {showLinkAccountAction && canLinkAccount(account) && (
                        <DropdownMenuItem onClick={() => setShowLinkAccountDrawer(true)}>
                            <LuLink className="mr-2 h-4 w-4" />
                            Link account
                        </DropdownMenuItem>
                    )}
                    {showLiveAccountActions && canChangeTradeStatus && (
                        <DropdownMenuItem onClick={onChangeTradeStatus ?? (() => setShowTradeStatusModal(true))}>
                            <LuCircleDollarSign className="mr-2 h-4 w-4" />
                            {tradingSuspended ? 'Enable' : 'Suspend'} trading
                        </DropdownMenuItem>
                    )}
                    {showLiveAccountActions && (
                        <DropdownMenuItem onClick={() => setShowChangeCustodyAccountDrawer(true)}>
                            <LuLink className="mr-2 h-4 w-4" />
                            Change linked custody account
                        </DropdownMenuItem>
                    )}
                    {showLiveAccountActions && (
                        <DropdownMenuItem onClick={() => setShowChangeAccountOwnerDrawer(true)}>
                            <LuUserCog className="mr-2 h-4 w-4" />
                            Change account owner
                        </DropdownMenuItem>
                    )}
                    <CopyGuidDropdownMenuItem guid={account.accountGUID} />
                    {showLiveAccountActions && (
                        <DropdownMenuItem onClick={() => setShowCloseAccountModal(true)} className="text-red-600">
                            <LuX className="mr-2 h-4 w-4" />
                            Close account
                        </DropdownMenuItem>
                    )}
                    {showDeleteAction && (
                        <DropdownMenuItem onClick={() => onDelete?.(account)} className="text-red-600">
                            <LuTrash2 className="mr-2 h-4 w-4" />
                            Delete account
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            {showLinkAccountDrawer && (
                <LinkAccountDrawer account={account} onClose={() => setShowLinkAccountDrawer(false)} />
            )}
            {showTradeStatusModal && (
                <AccountTradeStatusModal
                    householdGUID={globalAccountGUID}
                    account={account}
                    onClose={() => setShowTradeStatusModal(false)}
                />
            )}
            {showChangeCustodyAccountDrawer && (
                <ChangeLinkedCustodyAccountDrawer
                    householdGUID={globalAccountGUID}
                    account={account}
                    onClose={() => setShowChangeCustodyAccountDrawer(false)}
                />
            )}
            {showChangeAccountOwnerDrawer && (
                <ChangeAccountOwnerDrawer
                    householdGUID={globalAccountGUID}
                    account={account}
                    onClose={() => setShowChangeAccountOwnerDrawer(false)}
                />
            )}
            {showCloseAccountModal && (
                <CloseAccountValidationModal
                    householdGUID={globalAccountGUID}
                    account={account}
                    onClose={() => setShowCloseAccountModal(false)}
                />
            )}
        </>
    );
};
